// src/admin/ServiceRequestsReview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  readAffiliateCommissionByLevel,
  readAffiliateTotalCommission,
} from "../utils/affiliateCommission";
import {
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiZoomIn,
  FiChevronRight,
  FiFileText,
  FiPhone,
  FiMail,
  FiUser,
} from "react-icons/fi";

/* ───────────────────────── constants / helpers ───────────────────────── */
const UI_CURRENCY = "LKR";
const PLACEHOLDER = "/placeholder.gif";

const STATUS_OPTIONS = [
  { v: "new", label: "New" },
  { v: "accepted", label: "Accepted" },
  { v: "in_progress", label: "In Progress" },
  { v: "completed", label: "Completed" },
  { v: "declined", label: "Declined" },
  { v: "all", label: "All (recent)" },
];

const fmt = (n, cur = UI_CURRENCY) =>
  `${cur} ${Number(n || 0).toLocaleString()}`;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function normalizeSellerPayment(storeData = {}, fallbackSellerName = "") {
  const payment = storeData.paymentDetails || {};
  return {
    sellerUid: storeData.instructorId || "",
    sellerName: storeData.displayName || fallbackSellerName || "Instructor",
    sellerSlug: storeData.slug || "",
    bankName: payment.bankName || "",
    accountHolder: payment.accountHolder || "",
    accountNumber: payment.accountNumber || "",
    branch: payment.branch || "",
    phone: payment.phone || "",
    whatsapp: payment.whatsapp || "",
    instructions: payment.instructions || "",
    paymentQrCodeUrl: payment.qrCodeUrl || "",
  };
}

function clsx(...a) {
  return a.filter(Boolean).join(" ");
}

/* ───────────────────────── notifications (simple) ───────────────────────── */
async function notifyUser(uid, payload) {
  try {
    if (!uid) return;
    await addDoc(collection(db, "notifications"), {
      userId: uid,
      ...payload,
      createdAt: serverTimestamp(),
      read: false,
      seen: false,
    });
    // mirror into users/{uid}/notifications
    await addDoc(collection(db, "users", uid, "notifications"), {
      ...payload,
      createdAt: serverTimestamp(),
      read: false,
      seen: false,
    });
  } catch (e) {
    console.error("notifyUser failed", e);
  }
}

/* ───────────────────────── Drive + Image helpers ───────────────────────── */
function extractDriveId(rawUrl = "") {
  const url = (rawUrl || "").trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (m && m[1]) return m[1];
    const q = u.searchParams.get("id");
    if (q) return q;
  } catch {}
  return null;
}
function buildDriveImageCandidates(thumbnailUrl) {
  const id = extractDriveId(thumbnailUrl);
  if (!id) return [];
  return [
    `https://drive.usercontent.google.com/uc?id=${id}&export=view`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    `https://drive.google.com/uc?export=download&id=${id}`,
    `https://lh3.googleusercontent.com/d/${id}=w1600-h900-no`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w1600`,
  ];
}
function buildCoverCandidates(p = {}) {
  const set = new Set();
  const add = (v) => typeof v === "string" && v.trim() && set.add(v.trim());

  add(p.thumbnailUrl);
  add(p.imageUrl);
  add(p.coverUrl);
  add(p.image);
  add(p.cover);
  add(p.photo);

  ["images", "gallery", "photos", "serviceImages", "pictures"].forEach((k) => {
    const arr = p[k];
    if (!Array.isArray(arr)) return;
    arr.forEach((it) => {
      if (typeof it === "string") add(it);
      else if (it && typeof it === "object") add(it.url || it.src || it.href);
    });
  });

  const chain = [];
  for (const u of set) {
    const drive = buildDriveImageCandidates(u);
    if (drive.length) chain.push(...drive);
    else chain.push(u);
  }
  if (chain.length === 0) chain.push(PLACEHOLDER);
  return [...new Set(chain)];
}

function ImageChain({ sources = [PLACEHOLDER], alt = "" }) {
  const [idx, setIdx] = useState(0);
  const [src, setSrc] = useState(sources[0] || PLACEHOLDER);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setIdx(0);
    setSrc(sources[0] || PLACEHOLDER);
    setLoaded(false);
  }, [sources]);

  useEffect(() => {
    setLoaded(false);
    if (!sources.length) return;
    let alive = true;
    const candidate = sources[idx] || PLACEHOLDER;
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = candidate;
    img.onload = () => alive && (setSrc(candidate), setLoaded(true));
    img.onerror = () => {
      if (!alive) return;
      if (idx < sources.length - 1) setIdx((n) => n + 1);
      else setLoaded(true);
    };
    return () => (alive = false);
  }, [idx, sources]);

  return (
    <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 w-14 h-14">
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-100" />
      )}
      <img
        src={src}
        alt={alt}
        className={clsx(
          "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
          loaded ? "opacity-100" : "opacity-0",
        )}
        loading="eager"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

/* ───────────────────────── small UI components ───────────────────────── */
function StatusPill({ value }) {
  const tone =
    value === "new"
      ? "bg-amber-100 text-amber-700"
      : value === "accepted"
        ? "bg-blue-100 text-blue-700"
        : value === "in_progress"
          ? "bg-indigo-100 text-indigo-700"
          : value === "completed"
            ? "bg-green-100 text-green-700"
            : value === "declined"
              ? "bg-rose-100 text-rose-700"
              : "bg-gray-100 text-gray-700";
  const Icon =
    value === "declined"
      ? FiXCircle
      : value === "new"
        ? FiClock
        : FiCheckCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`}
    >
      <Icon /> {value || "unknown"}
    </span>
  );
}

function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
      <div className={`bg-white rounded-2xl shadow-xl border w-full ${max}`}>
        <div className="p-5">{children}</div>
        <div className="p-3 text-center">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border hover:bg-gray-50 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── main component ───────────────────────── */
export default function ServiceRequestsReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [statusFilter, setStatusFilter] = useState("new");
  const [search, setSearch] = useState("");

  // modals
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [detailReq, setDetailReq] = useState(null);
  const [confirmAccept, setConfirmAccept] = useState({ open: false, id: "" });
  const [decline, setDecline] = useState({ open: false, id: "", reason: "" });
  const [invoice, setInvoice] = useState({
    open: false,
    id: "",
    amount: "",
    currency: UI_CURRENCY,
    note: "",
  });

  const adminUid = getAuth().currentUser?.uid || "admin";

  // live load
  useEffect(() => {
    setLoading(true);
    let qRef;
    if (statusFilter === "all") {
      qRef = query(
        collection(db, "service_requests"),
        orderBy("createdAt", "desc"),
        limit(300),
      );
    } else {
      qRef = query(
        collection(db, "service_requests"),
        where("status", "==", statusFilter),
        orderBy("createdAt", "desc"),
        limit(300),
      );
    }
    const unsub = onSnapshot(qRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setRows(list);
      setLoading(false);
    });
    return () => unsub();
  }, [statusFilter]);

  // fetch service thumbnails on-demand
  const [svcThumbs, setSvcThumbs] = useState({});
  useEffect(() => {
    (async () => {
      const ids = Array.from(
        new Set(rows.map((r) => r.serviceId).filter(Boolean)),
      ).slice(0, 40); // light cap
      const out = {};
      await Promise.all(
        ids.map(async (sid) => {
          try {
            const s = await getDoc(doc(db, "courses", sid));
            if (s.exists()) out[sid] = buildCoverCandidates(s.data());
          } catch {}
        }),
      );
      setSvcThumbs(out);
    })();
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.id, r.serviceTitle, r.buyerName, r.buyerEmail, r.buyerPhone, r.message]
        .filter(Boolean)
        .some((t) => String(t).toLowerCase().includes(term)),
    );
  }, [rows, search]);

  /* ───────────────────────── actions ───────────────────────── */
  async function doAccept() {
    const id = confirmAccept.id;
    if (!id) return;
    try {
      const ref = doc(db, "service_requests", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Request not found");
      const r = snap.data();

      const batch = writeBatch(db);
      batch.update(ref, {
        status: "accepted",
        acceptedAt: serverTimestamp(),
        acceptedBy: adminUid,
      });

      // notify buyer & seller
      await Promise.all([
        notifyUser(r.buyerId, {
          title: "Request accepted ✅",
          body: `Your request for “${
            r.serviceTitle || "a service"
          }” was accepted. We’ll contact you shortly.`,
          type: "success",
          extra: { requestId: id, serviceId: r.serviceId },
        }),
        notifyUser(r.sellerId, {
          title: "New accepted request",
          body: `A request for “${
            r.serviceTitle || "your service"
          }” is accepted. Please follow up.`,
          type: "info",
          extra: { requestId: id, serviceId: r.serviceId },
        }),
      ]);

      await batch.commit();
      setConfirmAccept({ open: false, id: "" });
    } catch (e) {
      console.error("Accept failed:", e);
      alert(`Accept failed: ${e.message}`);
    }
  }

  async function doDecline() {
    const { id, reason } = decline;
    if (!id || !reason.trim()) return;
    try {
      const ref = doc(db, "service_requests", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Request not found");
      const r = snap.data();

      await updateDoc(ref, {
        status: "declined",
        declinedAt: serverTimestamp(),
        declinedBy: adminUid,
        declineReason: reason.trim(),
      });

      await notifyUser(r.buyerId, {
        title: "Request declined",
        body: `Your request for “${
          r.serviceTitle || "a service"
        }” was declined. Reason: ${reason.trim()}.`,
        type: "error",
        extra: { requestId: id, serviceId: r.serviceId },
      });

      setDecline({ open: false, id: "", reason: "" });
    } catch (e) {
      console.error("Decline failed:", e);
      alert(`Decline failed: ${e.message}`);
    }
  }

  // Prepare defaults for invoice modal (from service doc or request)
  async function openInvoice(req) {
    let suggested = 0;
    try {
      if (req.serviceId) {
        const s = await getDoc(doc(db, "courses", req.serviceId));
        if (s.exists()) {
          const d = s.data() || {};
          if ((d.pricingType || d.typePricing) === "hourly") {
            const hr = Number(d.hourlyRate || 0);
            const mh = Number(d.minHours || 1);
            suggested = round2(hr * (mh || 1));
          } else if (d.price != null) {
            suggested = round2(Number(d.price || 0));
          }
        }
      }
    } catch {}
    setInvoice({
      open: true,
      id: req.id,
      amount: suggested ? String(suggested) : "",
      currency: UI_CURRENCY,
      note: "",
    });
  }

  async function createOrderFromRequest() {
    const { id, amount, currency, note } = invoice;
    if (!id) return;
    const amt = Number(amount);
    if (!(amt > 0)) {
      alert("Enter a valid amount.");
      return;
    }
    try {
      const reqRef = doc(db, "service_requests", id);
      const rs = await getDoc(reqRef);
      if (!rs.exists()) throw new Error("Request not found");
      const r = rs.data();
      let sellerPaymentSnapshot = null;
      if (r.sellerId) {
        const storeSnap = await getDoc(doc(db, "stores", r.sellerId));
        if (storeSnap.exists()) {
          sellerPaymentSnapshot = normalizeSellerPayment(
            storeSnap.data() || {},
            r.serviceTitle || "",
          );
        }
      }

      const affiliateByLevel = readAffiliateCommissionByLevel(r);
      const affiliateTotalCommissionAmount = readAffiliateTotalCommission(r);

      // create order document (type: service)
      const oRef = await addDoc(collection(db, "orders"), {
        type: "service",
        kind: "service",
        serviceId: r.serviceId || null,
        serviceTitle: r.serviceTitle || "",
        sellerUserId: r.sellerId || null,
        instructorUserId: r.sellerId || null,
        buyerUid: r.buyerId || null,
        userId: r.buyerId || null,
        buyer: {
          name: r.buyerName || "",
          phone: r.buyerPhone || "",
          email: r.buyerEmail || "",
        },
        status: "pending", // waiting for payment slip
        amount: amt,
        currency: currency || UI_CURRENCY,
        amountDetail: {
          total: amt,
          currency: currency || UI_CURRENCY,
        },
        createdAt: serverTimestamp(),
        payment: {
          reviewed: false,
          slipUrl: "",
          declineReason: null,
        },
        sellerPaymentSnapshot,
        commissionAmount: affiliateTotalCommissionAmount,
        affiliateEnabled: r.affiliateEnabled === true,
        affiliateCommissionAmount: affiliateTotalCommissionAmount,
        affiliateLevel1Amount: affiliateByLevel[1],
        affiliateLevel2Amount: affiliateByLevel[2],
        affiliateLevel3Amount: affiliateByLevel[3],
        affiliateCommissionByLevel: affiliateByLevel,
        affiliateTotalCommissionAmount,
        affiliateReferrerUid: r.affiliateReferrerUid || r.referrerUserId || null,
        affiliateReferralCode: r.affiliateReferralCode || r.referralCode || null,
        affiliateSettlementId: null,
        affiliateSettlementStatus: null,
        referralCode: r.affiliateReferralCode || r.referralCode || null,
        referrerUserId: r.affiliateReferrerUid || r.referrerUserId || null,
        // optional linkage
        requestId: id,
        adminNote: (note || "").trim() || null,
      });

      // link back to request
      await updateDoc(reqRef, {
        orderId: oRef.id,
        invoicedAt: serverTimestamp(),
        status: "accepted", // keep accepted or move to another label if you prefer
      });

      // notify buyer
      await notifyUser(r.buyerId, {
        title: "Invoice created 🧾",
        body: `We created an order for “${
          r.serviceTitle || "your service"
        }”. Please upload payment slip to proceed.`,
        type: "info",
        extra: { orderId: oRef.id, requestId: id, serviceId: r.serviceId },
      });

      setInvoice({
        open: false,
        id: "",
        amount: "",
        currency: UI_CURRENCY,
        note: "",
      });
      alert("Order created. You can track it under Orders.");
    } catch (e) {
      console.error("Create order failed:", e);
      alert(`Create order failed: ${e.message}`);
    }
  }

  /* ───────────────────────── render ───────────────────────── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f8fa] via-[#eef3ff] to-[#f6f2ff]">
      <div className="w-full py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        <div className="rounded-3xl bg-white border shadow-sm p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl md:text-2xl font-black tracking-tight">
              Service Requests Review
            </h1>

            <div className="flex flex-wrap items-center gap-2">
              <select
                className="px-3 py-2 rounded-xl border text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.label}
                  </option>
                ))}
              </select>

              <div className="relative">
                <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by request ID, buyer, phone, email, service…"
                  className="pl-9 pr-3 py-2 rounded-xl border text-sm w-80"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left py-2">Service</th>
                  <th className="text-left py-2">Buyer</th>
                  <th className="text-left py-2">Request</th>
                  <th className="text-left py-2">Attachment</th>
                  <th className="text-left py-2">Pricing</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-gray-400">
                      No service requests
                    </td>
                  </tr>
                )}

                {filtered.map((r) => {
                  const covers = svcThumbs[r.serviceId] || [PLACEHOLDER];
                  const priceLine =
                    (r.pricingType || "").toLowerCase() === "hourly"
                      ? `${fmt(r.hourlyRate)} / hr • min ${r.minHours || 1}h`
                      : fmt(r.fixedPrice || r.price || 0);

                  return (
                    <tr key={r.id} className="align-top">
                      {/* Service */}
                      <td className="py-3 pr-4">
                        <div className="flex items-start gap-3">
                          <ImageChain sources={covers} alt={r.serviceTitle} />
                          <div className="min-w-0">
                            <div className="font-semibold line-clamp-1">
                              {r.serviceTitle || "Service"}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              #{r.id}
                            </div>
                            {r.serviceId ? (
                              <Link
                                to={`/services/${r.serviceId}`}
                                className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline mt-0.5"
                                target="_blank"
                                rel="noreferrer"
                              >
                                View page <FiChevronRight />
                              </Link>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      {/* Buyer */}
                      <td className="py-3 pr-4">
                        <div className="font-medium flex items-center gap-1">
                          <FiUser className="text-gray-400" />
                          {r.buyerName || "-"}
                        </div>
                        <div className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                          <FiPhone className="text-gray-400" />
                          {r.buyerPhone || "-"}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <FiMail className="text-gray-400" />
                          {r.buyerEmail || ""}
                        </div>
                      </td>

                      {/* Request snippet */}
                      <td className="py-3 pr-4 max-w-[320px]">
                        <div className="line-clamp-3 text-gray-700">
                          {r.message || "-"}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1">
                          {r.preferredStart
                            ? `Preferred: ${r.preferredStart}`
                            : "No date preference"}
                        </div>
                        <button
                          className="mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg border hover:bg-gray-50"
                          onClick={() => setDetailReq(r)}
                        >
                          <FiFileText /> View details
                        </button>
                      </td>

                      {/* Attachment */}
                      <td className="py-3 pr-4">
                        {r.attachmentUrl ? (
                          <button
                            onClick={() => setAttachmentUrl(r.attachmentUrl)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-gray-50"
                          >
                            <FiZoomIn /> Preview
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>

                      {/* Pricing */}
                      <td className="py-3 pr-4">
                        <div className="font-semibold">{priceLine}</div>
                        <div className="text-[11px] text-gray-400">
                          {(r.pricingType || "").toLowerCase() || "fixed"}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3 pr-4">
                        <StatusPill value={r.status || "new"} />
                      </td>

                      {/* Actions */}
                      <td className="py-3 pl-4 text-right">
                        <div className="inline-flex flex-wrap gap-2 justify-end">
                          <button
                            className={clsx(
                              "px-3 py-1.5 rounded-xl text-white font-semibold",
                              r.status === "new"
                                ? "bg-green-600 hover:bg-green-700"
                                : "bg-gray-300 cursor-not-allowed",
                            )}
                            onClick={() =>
                              setConfirmAccept({ open: true, id: r.id })
                            }
                            disabled={r.status !== "new"}
                            title="Accept request"
                          >
                            Accept
                          </button>

                          <button
                            className="px-3 py-1.5 rounded-xl font-semibold border text-rose-700 border-rose-200 hover:bg-rose-50"
                            onClick={() =>
                              setDecline({ open: true, id: r.id, reason: "" })
                            }
                            title="Decline with reason"
                          >
                            Decline
                          </button>

                          <button
                            className="px-3 py-1.5 rounded-xl font-semibold border text-blue-700 border-blue-200 hover:bg-blue-50"
                            onClick={() => openInvoice(r)}
                            title="Create order / invoice"
                          >
                            Create Order
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Attachment modal */}
      <Modal
        open={!!attachmentUrl}
        onClose={() => setAttachmentUrl("")}
        max="max-w-4xl"
      >
        <div className="text-lg font-bold mb-3">Attachment</div>
        {String(attachmentUrl).toLowerCase().includes(".pdf") ? (
          <a
            href={attachmentUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            Open PDF in new tab
          </a>
        ) : (
          <img
            src={attachmentUrl}
            alt="attachment"
            className="max-h-[70vh] mx-auto rounded-xl border"
          />
        )}
      </Modal>

      {/* Details modal */}
      <Modal
        open={!!detailReq}
        onClose={() => setDetailReq(null)}
        max="max-w-3xl"
      >
        {detailReq && (
          <>
            <div className="text-lg font-bold mb-2">Request details</div>
            <div className="text-sm text-gray-700">
              <div className="font-semibold">
                {detailReq.serviceTitle || "Service"}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                #{detailReq.id}
              </div>
            </div>

            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border p-3">
                <div className="text-xs text-gray-500">Buyer</div>
                <div className="font-medium flex items-center gap-2 mt-0.5">
                  <FiUser className="text-gray-400" />
                  {detailReq.buyerName || "-"}
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-2 mt-1">
                  <FiPhone className="text-gray-400" />
                  {detailReq.buyerPhone || "-"}
                </div>
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <FiMail className="text-gray-400" />
                  {detailReq.buyerEmail || "-"}
                </div>
              </div>

              <div className="rounded-xl border p-3">
                <div className="text-xs text-gray-500">Preferences</div>
                <div className="text-sm mt-1">
                  {detailReq.preferredStart || "No date preference"}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Pricing: {(detailReq.pricingType || "fixed").toLowerCase()}
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-xl border p-3">
              <div className="text-xs text-gray-500">Message</div>
              <div className="text-sm mt-1 whitespace-pre-wrap">
                {detailReq.message || "-"}
              </div>
            </div>

            {detailReq.attachmentUrl && (
              <div className="mt-3">
                <button
                  onClick={() => setAttachmentUrl(detailReq.attachmentUrl)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border hover:bg-gray-50 text-sm"
                >
                  <FiZoomIn /> Open attachment
                </button>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Accept confirm modal */}
      <Modal
        open={confirmAccept.open}
        onClose={() => setConfirmAccept({ open: false, id: "" })}
      >
        <div className="text-lg font-bold">Accept request?</div>
        <p className="text-sm text-gray-600 mt-1">
          This will mark the request as <b>accepted</b> and notify both the
          buyer and the seller to proceed.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => setConfirmAccept({ open: false, id: "" })}
            className="px-3 py-2 rounded-xl border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={doAccept}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
          >
            Accept
          </button>
        </div>
      </Modal>

      {/* Decline modal */}
      <Modal
        open={decline.open}
        onClose={() => setDecline({ open: false, id: "", reason: "" })}
      >
        <div className="text-lg font-bold">Decline request</div>
        <p className="text-sm text-gray-600 mt-1">
          Provide a reason. The status will be set to <b>declined</b>, and the
          buyer will be notified.
        </p>
        <textarea
          value={decline.reason}
          onChange={(e) =>
            setDecline((s) => ({ ...s, reason: e.target.value }))
          }
          rows={4}
          placeholder="Reason (e.g., scope out of capacity / not a good fit / missing details)…"
          className="mt-3 w-full rounded-xl border p-2 text-sm"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            onClick={() => setDecline({ open: false, id: "", reason: "" })}
            className="px-3 py-2 rounded-xl border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={!decline.reason.trim()}
            onClick={doDecline}
            className={clsx(
              "px-3 py-2 rounded-xl text-white font-semibold",
              decline.reason.trim()
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-rose-300",
            )}
          >
            Decline
          </button>
        </div>
      </Modal>

      {/* Create Order / Invoice modal */}
      <Modal
        open={invoice.open}
        onClose={() =>
          setInvoice({
            open: false,
            id: "",
            amount: "",
            currency: UI_CURRENCY,
            note: "",
          })
        }
      >
        <div className="text-lg font-bold">Create order / invoice</div>
        <p className="text-sm text-gray-600 mt-1">
          This will create an <b>order</b> for the service and notify the buyer
          to upload a payment slip.
        </p>
        <div className="mt-4 grid gap-3">
          <div className="grid grid-cols-[1fr,120px] gap-2">
            <div>
              <label className="text-xs text-gray-600 font-semibold">
                Amount
              </label>
              <input
                value={invoice.amount}
                onChange={(e) =>
                  setInvoice((s) => ({ ...s, amount: e.target.value }))
                }
                placeholder="e.g., 25,000"
                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                inputMode="decimal"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 font-semibold">
                Currency
              </label>
              <select
                value={invoice.currency}
                onChange={(e) =>
                  setInvoice((s) => ({ ...s, currency: e.target.value }))
                }
                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
              >
                <option value="LKR">LKR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-semibold">
              Note (optional)
            </label>
            <textarea
              value={invoice.note}
              onChange={(e) =>
                setInvoice((s) => ({ ...s, note: e.target.value }))
              }
              rows={3}
              className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
              placeholder="Any admin/seller note…"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() =>
              setInvoice({
                open: false,
                id: "",
                amount: "",
                currency: UI_CURRENCY,
                note: "",
              })
            }
            className="px-3 py-2 rounded-xl border hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={createOrderFromRequest}
            className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            Create order
          </button>
        </div>
      </Modal>
    </div>
  );
}
